import React, { useEffect, useState } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import { FunnelIcon } from "@heroicons/react/24/outline";
import IconButton from "../../../components/IconButton";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import ButtonInternalLink from "../../../components/ButtonInternalLink";
import SelectInput from "../../../components/SelectInput";
import Loading from "../../../components/Loading";
import { apollo } from "../../../utils/apollo";
import {
  GET_POLICY_LIST,
  GET_POLICY_STATUS_COUNT,
} from "../../../utils/graphql";
import Tabs from "../../../components/Tabs";
import { POLICY_LIST_STATUS_FOR_FILTER } from "../../../utils/constants";
import CompactPolicyList from "./components/CompactPolicyList";
import PaginationFull from "../../../components/PaginationFull";
import TextInput from "../../../components/TextInput";
import useIntegration from "../../../utils/useIntegration";
function PolicyList() {
  const { t } = useTranslation();
  const [sort, setSort] = useState({
    field: "id",
    isAscending: false,
  });
  const [page, setPage] = useState(1);
  const [showFilter, setShowFilter] = useState(true);
  const [filter, setFilter] = useState({
    status: "",
    test: "",
    policyUuid: "",
    integrationUuid: "",
  });
  const [statusTabs, setStatusTabs] = useState(POLICY_LIST_STATUS_FOR_FILTER);
  const {
    integrationList: integrations,
    integrationUuid: globalIntegrationUuid,
    integration,
  } = useIntegration();
  const isShafafiya = integration?.integrationType === "shafafiya";
  useEffect(() => setPage(1), [filter, sort]);
  useEffect(() => {
    if (globalIntegrationUuid) {
      setFilter((prev) => ({
        ...prev,
        integrationUuid: globalIntegrationUuid,
      }));
    }
  }, [globalIntegrationUuid]);
  useQuery(
    ["policy_status_count", globalIntegrationUuid],
    async () => {
      const { data } = await apollo.query({
        query: GET_POLICY_STATUS_COUNT,
        variables: {
          integrationUuid: globalIntegrationUuid,
        },
      });
      return data?.getPolicyStatusCount;
    },
    {
      keepPreviousData: true,
      refetchInterval: 60000,
      onSettled: (data) => {
        if (data && Array.isArray(data)) {
          let updatedStatusTabs = [...statusTabs];
          data.forEach((item) => {
            let foundIndex = updatedStatusTabs.findIndex(
              (tab) => tab.column === item.status,
            );
            if (foundIndex !== -1) {
              updatedStatusTabs[foundIndex].count = parseInt(item.count);
            }
          });
          setStatusTabs(updatedStatusTabs);
        }
      },
    },
  );
  const fetchPolicies = useQuery(
    [
      "policy_list",
      {
        sort,
        page,
        filter,
      },
    ],
    async () => {
      const { data } = await apollo.query({
        query: GET_POLICY_LIST,
        variables: {
          sortBy: sort.field?.toUpperCase(),
          sortDirection: sort.isAscending ? "ASC" : "DESC",
          page,
          first: 15,
          filter: {
            status: filter.status,
            // test: filter.test,
            policyUuid: filter.policyUuid,
            integrationUuid: filter.integrationUuid,
          },
        },
      });
      return data?.getPolicyList || null;
    },
    {
      keepPreviousData: false,
      refetchInterval: 10000,
    },
  );
  const records = fetchPolicies?.data || null;
  const handleChangeSort = (field) => {
    if (sort.field === field) {
      setSort((prevState) => ({
        ...prevState,
        isAscending: !prevState.isAscending,
      }));
    } else {
      setSort({
        field: field,
        isAscending: true,
      });
    }
  };
  return (
    <DashboardLayout>
      <PageHeader
        title={
          isShafafiya
            ? t("policy.shafafiya_page_title", "Members")
            : t("policy.page_title")
        }
        description={
          isShafafiya
            ? t(
                "policy.shafafiya_page_description",
                "View and manage members directly.",
              )
            : t("policy.page_description")
        }
      >
        <div className="flex items-center gap-3 flex-wrap">
          <IconButton onClick={() => setShowFilter(!showFilter)}>
            <FunnelIcon className="w-5 h-5 text-primary-500" />
          </IconButton>
          <div className="flex items-center gap-3 flex-wrap">
            <ButtonInternalLink href="/policy/import">
              {isShafafiya
                ? t("policy.import_members")
                : t("policy.import_policies")}
            </ButtonInternalLink>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <ButtonInternalLink href="/policy/create">
              {isShafafiya
                ? t("policy.create_member", "Create Member")
                : t("policy.create_policy")}
            </ButtonInternalLink>
          </div>
        </div>
      </PageHeader>
      {showFilter && (
        <div className="layer-flat px-6 py-6 mb-8 grid grid-cols-1 md:grid-cols-12 gap-6 transition-all duration-300">
          <div className="col-span-4 lg:col-span-3">
            <TextInput
              name="policy-ref-filter"
              label={
                isShafafiya
                  ? t("policy.shafafiya_ref_eid_uid")
                  : t("policy.policy_id_reference")
              }
              id="policy-ref-filter"
              get={filter.policyUuid}
              set={(v) =>
                setFilter({
                  ...filter,
                  policyUuid: v,
                })
              }
            />
          </div>
        </div>
      )}
      <Tabs
        tabs={statusTabs.map((s) => ({ ...s, name: t(s.name) }))}
        get={filter?.status}
        set={(v) =>
          setFilter({
            ...filter,
            status: v,
          })
        }
      />
      {fetchPolicies.isLoading && <Loading />}
      {fetchPolicies.isSuccess && records && (
        <>
          <CompactPolicyList
            handleChangeSort={handleChangeSort}
            records={records}
            sort={sort}
          />
          {/*<Pagination paginator={records?.paginatorInfo} get={page} set={setPage}/>*/}
          <PaginationFull
            paginator={records?.paginatorInfo}
            get={page}
            set={setPage}
          />
        </>
      )}
    </DashboardLayout>
  );
}
export default PolicyList;
